import React, { SyntheticEvent, useCallback } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import Button from "@atlaskit/button";
import Form, {
    Field,
    FormSection,
    FormFooter,
    ErrorMessage
} from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Box, Flex, Stack, xcss, Text } from "@atlaskit/primitives";
import TextField from "@atlaskit/textfield";

import { PageProjectDetailsProps } from "../../../common/types";

import {
    JIRA_MAX_PROJECT_NAME_LENGTH,
    JIRA_MIN_PROJECT_NAME_LENGTH,
    PROJECT_NAME_TEXT_FIELD
} from "./constants";
import messages from "./messages";

export const LeftHandSidePageProjectDetails: React.FC<
    PageProjectDetailsProps
> = ({
    onClickNext,
    currentProjectName,
    onProjectNameChange,
    defaultProjectName
}) => {
    const { formatMessage } = useIntl();

    const handleOnSubmit = () => {
        onClickNext();
    };

    const validateProjectName = useCallback(
        (value: string | undefined) => {
            // If the project name is undefined, we don't need to validate it since we use the default project name
            if (value === undefined) {
                return;
            }
            const trimmedValueLength = value?.trim().length ?? 0;
            if (trimmedValueLength < JIRA_MIN_PROJECT_NAME_LENGTH) {
                return formatMessage(
                    messages.projectNameMinCharacterLengthError,
                    {
                        _minCharacters: JIRA_MIN_PROJECT_NAME_LENGTH,
                        get minCharacters() {
                            return this["_minCharacters"];
                        },
                        set minCharacters(value) {
                            this["_minCharacters"] = value;
                        }
                    }
                );
            }
            if (trimmedValueLength > JIRA_MAX_PROJECT_NAME_LENGTH) {
                return formatMessage(
                    messages.projectNameMaxCharacterLengthError,
                    {
                        maxCharacters: JIRA_MAX_PROJECT_NAME_LENGTH
                    }
                );
            }
        },
        [formatMessage]
    );

    const handleProjectNameChanged = useCallback(
        (onChange: (e: SyntheticEvent<HTMLInputElement>) => void) =>
            (e: SyntheticEvent<HTMLInputElement>) => {
                onProjectNameChange(e.currentTarget.value);
                onChange(e);
            },
        [onProjectNameChange]
    );

    return (
        <Flex xcss={containerStyles}>
            <Form onSubmit={handleOnSubmit}>
                {({ formProps }) => (
                    <form {...formProps} noValidate>
                        <Stack spread="space-between" xcss={containerStyles}>
                            <Stack>
                                <Stack space="space.150">
                                    <Heading size="xxlarge">
                                        {formatMessage(messages.title)}
                                    </Heading>
                                    <Box xcss={subtextStyles}>
                                        <FormattedMessage
                                            {...messages.bodyLineOne}
                                        />
                                    </Box>
                                </Stack>
                                <FormSection>
                                    <Stack space="space.300">
                                        <Field
                                            name={PROJECT_NAME_TEXT_FIELD}
                                            label={formatMessage(
                                                messages.projectFormField
                                            )}
                                            validate={validateProjectName}
                                        >
                                            {({ fieldProps, error, meta }) => (
                                                <>
                                                    <TextField
                                                        {...fieldProps}
                                                        autoComplete="off"
                                                        defaultValue={
                                                            defaultProjectName
                                                        }
                                                        value={
                                                            currentProjectName
                                                        }
                                                        onChange={handleProjectNameChanged(
                                                            fieldProps.onChange
                                                        )}
                                                    />
                                                    {fieldProps.isInvalid &&
                                                        meta.touched && (
                                                            <ErrorMessage>
                                                                {error}
                                                            </ErrorMessage>
                                                        )}
                                                </>
                                            )}
                                        </Field>
                                        <Stack
                                            xcss={projectExampleStyles}
                                            space="space.100"
                                        >
                                            <Text
                                                color="color.text.subtlest"
                                                as="strong"
                                            >
                                                {formatMessage(
                                                    messages.projectExampleHeading
                                                )}
                                            </Text>
                                            <Text
                                                color="color.text.subtlest"
                                                as="p"
                                            >
                                                {formatMessage(
                                                    messages.projectExamples
                                                )}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                </FormSection>
                            </Stack>
                            <FormFooter>
                                <Button type="submit" appearance="primary">
                                    {formatMessage(messages.getStartedBtn)}
                                </Button>
                            </FormFooter>
                        </Stack>
                    </form>
                )}
            </Form>
        </Flex>
    );
};

const containerStyles = xcss({
    height: "100%"
});

const subtextStyles = xcss({
    fontSize: "font.body.medium",
    color: "color.text.subtle"
});

const projectExampleStyles = xcss({
    backgroundColor: "color.background.input.hovered",
    borderRadius: "border.radius.100",
    padding: "space.100"
});
